import axios from "axios";
import {
  isPortalEmbedded,
  isWorkflowUrl,
  portalBridgeFetch,
} from "./portalEmbedApi";
import {
  LICENSE_UNAVAILABLE_MESSAGE,
  refreshWorkflowToken,
  resolveAccessToken,
} from "./workflowSession";

/** Empresa,filial enviados no header tenantId das APIs Workflow/Protheus */
export const WORKFLOW_TENANT_ID = "01,01";

export function buildWorkflowAuthorizationValue({ token } = {}) {
  return token ? `Bearer ${token}` : "Bearer ";
}

export function buildWorkflowHeaders({ token } = {}) {
  return {
    Authorization: buildWorkflowAuthorizationValue({ token }),
    tenantId: WORKFLOW_TENANT_ID,
  };
}

function getStatus(error) {
  return Number(error?.response?.status || 0);
}

function decorateError(error) {
  if (getStatus(error) === 503) {
    error.isLicenseError = true;
    error.userMessage =
      error.response?.data?.message || LICENSE_UNAVAILABLE_MESSAGE;
    error.message = error.userMessage;
  }
  return error;
}

/**
 * Executa a requisição com o token atual; no 401 renova o token (uma vez,
 * compartilhado entre chamadas simultâneas) e repete a requisição.
 */
async function runWithTokenRetry(send, creds = {}) {
  const token = resolveAccessToken(creds.token);
  try {
    return await send(token);
  } catch (error) {
    if (getStatus(error) !== 401) throw decorateError(error);

    let newToken;
    try {
      newToken = await refreshWorkflowToken(token, creds);
    } catch (refreshError) {
      error.sessionExpired = Boolean(refreshError.sessionExpired);
      error.userMessage = refreshError.userMessage;
      error.message = refreshError.userMessage || error.message;
      throw error;
    }

    try {
      return await send(newToken);
    } catch (retryError) {
      throw decorateError(retryError);
    }
  }
}

export async function withWorkflowAuthRetry(requestFn, creds = {}) {
  return runWithTokenRetry(
    (token) => requestFn(buildWorkflowHeaders({ token })),
    creds,
  );
}

function isNetworkError(error) {
  return (
    !error?.response ||
    error?.code === "ERR_NETWORK" ||
    String(error?.message || "").includes("Network Error")
  );
}

function buildAxiosUrl(url, axiosConfig = {}) {
  if (!axiosConfig.params || typeof axiosConfig.params !== "object") {
    return url;
  }
  const parsed = new URL(url, window.location.href);
  for (const [key, value] of Object.entries(axiosConfig.params)) {
    if (value != null && value !== "") {
      parsed.searchParams.set(key, String(value));
    }
  }
  return parsed.toString();
}

async function bridgeWorkflowAxios(method, url, cfg, authHeaders, body) {
  const headers = {
    ...(cfg.headers || {}),
    ...authHeaders,
  };

  if (method === "POST" && body !== undefined) {
    headers["Content-Type"] = headers["Content-Type"] || "application/json";
  }

  const requestUrl = method === "GET" ? buildAxiosUrl(url, cfg) : url;
  const response = await portalBridgeFetch(requestUrl, {
    method,
    headers,
    body:
      method === "POST" && body !== undefined
        ? JSON.stringify(body)
        : undefined,
  });
  const data = await response.json().catch(() => ({}));
  const result = {
    data,
    status: response.status,
    headers: response.headers,
    config: cfg,
  };

  if (response.status >= 400) {
    const error = new Error(`Request failed with status code ${response.status}`);
    error.response = result;
    throw error;
  }
  return result;
}

async function sendWorkflowRequest(method, url, cfg, token, body) {
  const authHeaders = buildWorkflowHeaders({ token });

  if (isPortalEmbedded() && isWorkflowUrl(url)) {
    return bridgeWorkflowAxios(method, url, cfg, authHeaders, body);
  }

  try {
    if (method === "GET") {
      return await axios.get(url, {
        ...cfg,
        headers: { ...(cfg.headers || {}), ...authHeaders },
      });
    }
    return await axios.post(url, body, {
      ...cfg,
      headers: {
        "Content-Type": "application/json",
        ...(cfg.headers || {}),
        ...authHeaders,
      },
    });
  } catch (error) {
    if (isPortalEmbedded() && isWorkflowUrl(url) && isNetworkError(error)) {
      return bridgeWorkflowAxios(method, url, cfg, authHeaders, body);
    }
    throw error;
  }
}

function executeWorkflowAxios(method, url, axiosConfig = {}, creds = {}, body) {
  const cfg = { ...(axiosConfig || {}) };
  return runWithTokenRetry(
    (token) => sendWorkflowRequest(method, url, cfg, token, body),
    creds,
  );
}

export async function workflowAxiosGet(url, axiosConfig = {}, creds = {}) {
  return executeWorkflowAxios("GET", url, axiosConfig, creds);
}

export async function workflowAxiosPost(url, body, axiosConfig = {}, creds = {}) {
  return executeWorkflowAxios("POST", url, axiosConfig, creds, body);
}
